import { AppDispatch, AppThunk } from 'store';

import { MirrorService } from 'services/MirrorService';
import { AdsPlacement } from 'types/enums/AdsPlacement';
import { AdsPlatform } from 'types/enums/AdsPlatform';

import {
  FetchPublicUsersResponse,
  FinderApi,
  FinderFilters,
} from 'api/FinderApi';
import { TrackingApi } from 'api/TrackingApi';
import { UserTransferApi } from 'api/UserTransferApi';
import { getIsSessionAdsItemShown } from 'helpers/ads';
import { getScreenSizes } from 'helpers/device';
import { resetState as resetCommonState } from 'store/common/commonSlice';

import { setUsers as setVideocastUsers } from '../videoCast/videoCastSlice';
import {
  addUsers,
  setCurrentpage,
  setCurrentProfilesPackHash,
  setFilters,
  setFinderAdsVideo,
  setFinderUserVideo,
  setInitLoading,
  setLoading,
  setNext,
  setSympathies,
  setUsers as setFinderUsers,
} from './finderSlice';

const trackFinderLoad = (
  data: FetchPublicUsersResponse,
  currentPage: number,
  filters: FinderFilters
) => {
  const ts = Date.now();
  const { width: screenWidth, height: screenHeight } = getScreenSizes();
  const { city, ...restFilters } = filters;
  TrackingApi.trackFinderLoad({
    ts,
    profiles: data.data,
    screenWidth,
    screenHeight,
    paginationDepth: currentPage,
    nextUrl: data.next,
    filters: {
      ...restFilters,
      city_id: city.value,
    },
    profilesPackHash: data.profiles_pack_hash,
  }).catch((err) => err);
};

const resetExtendedFilters = (filters: FinderFilters): FinderFilters => {
  const EXTENDED_FILTERS_NAMES = [
    'marital_statuses',
    'ethnicities',
    'body_types',
    'preference_tags',
  ];

  return Object.entries(filters).reduce((acc, [key, value]) => {
    return {
      ...acc,
      [key]: EXTENDED_FILTERS_NAMES.includes(key) ? [] : value,
    };
  }, {}) as FinderFilters;
};

export const firstFetchPublicUsers = () => async (dispatch: AppDispatch) => {
  dispatch(setInitLoading(true));

  const { data } = await FinderApi.firstFetchPublicUsers();
  const currentPage = 1;

  dispatch(setSympathies(data.sympathies));
  dispatch(addUsers(data.data));
  dispatch(setFilters(data.params));
  dispatch(setNext(data.next));
  dispatch(setCurrentProfilesPackHash(data.profiles_pack_hash));
  dispatch(setInitLoading(false));
  trackFinderLoad(data, currentPage, data.params);
};

export const fetchPublicUsers =
  (payload: FinderFilters, isMobile = true): AppThunk =>
  async (dispatch: AppDispatch, getState) => {
    const state = getState();
    const isUserBuyer = state.auth.user?.is_buyer;
    const { isNativeAdsEnabled } = state.common.featureFlags;
    const { next, currentPage: prevCurrentPage } = state.finder;

    if (!next) {
      return;
    }

    dispatch(setLoading(true));

    const { data } = await FinderApi.fetchPublicUsers({
      filters: payload,
      url: next,
    });
    const currentPage = prevCurrentPage + 1;
    const defaultPageNumbers = isMobile ? 3 : 6;

    dispatch(addUsers(data.data));
    dispatch(setNext(data.next));
    dispatch(setCurrentpage(currentPage));
    dispatch(setCurrentProfilesPackHash(data.profiles_pack_hash));
    dispatch(setLoading(false));

    if (
      MirrorService.isVideosEnabled &&
      !MirrorService.isSimpleDates &&
      currentPage % defaultPageNumbers === 0
    ) {
      const isFinderAdsVideoShown = getIsSessionAdsItemShown(
        AdsPlacement.PicInPic
      );
      let adsVideo = null;
      const requestTs = Date.now();

      if (
        currentPage % 6 === 0 &&
        !isFinderAdsVideoShown &&
        isNativeAdsEnabled &&
        MirrorService.isSecondSiteGroup
      ) {
        try {
          adsVideo = await UserTransferApi.fetchFinderPicInPicBanner({
            platform: isMobile ? AdsPlatform.Mobile : AdsPlatform.Default,
          });
        } catch (err) {
          console.error(`ads banner ${AdsPlacement.PicInPic} load fail`);
        }

        if (adsVideo) {
          dispatch(setFinderUserVideo(null));

          dispatch(
            setFinderAdsVideo({
              ...adsVideo,
              requestTs,
            })
          );
        }
      }

      if (!adsVideo && isUserBuyer) {
        try {
          const userVideo = await FinderApi.fetchRandomUserWithVideo();

          if (userVideo) {
            dispatch(
              setFinderUserVideo({
                videoId: userVideo.video_id,
                videoUrl: userVideo.video_url,
                userId: userVideo.id,
                label: `${userVideo.username}, ${userVideo.age}`,
              })
            );
          }
        } catch (err) {
          console.error('finder user video load fail');
        }
      }
    }
    trackFinderLoad(data, currentPage, payload);
  };

export const refetchPublicUsersWithoutExtendedFilters = (
  payload: FinderFilters
): AppThunk =>
  async function refetchPublicUsersWithoutExtendedFiltersInner(dispatch) {
    dispatch(setLoading(true));

    const { data } = await FinderApi.fetchPublicUsers({
      filters: resetExtendedFilters(payload),
    });
    const currentPage = 1;

    dispatch(setFinderUsers(data.data));
    dispatch(setVideocastUsers([]));
    dispatch(setNext(data.next));
    dispatch(setFilters(resetExtendedFilters(payload)));
    dispatch(setCurrentpage(currentPage));
    dispatch(setCurrentProfilesPackHash(data.profiles_pack_hash));
    dispatch(setLoading(false));
    trackFinderLoad(data, currentPage, payload);
  };

export const fetchPublicUsersWithFilters =
  (payload: FinderFilters): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));

    const { data } = await FinderApi.fetchPublicUsers({
      filters: payload,
    });

    if (data.data.length === 0) {
      dispatch(refetchPublicUsersWithoutExtendedFilters(payload));

      return;
    }
    const currentPage = 1;

    dispatch(setFinderUsers(data.data));
    dispatch(setVideocastUsers([]));
    dispatch(setNext(data.next));
    dispatch(setFilters(payload));
    dispatch(setCurrentpage(currentPage));
    dispatch(setCurrentProfilesPackHash(data.profiles_pack_hash));
    dispatch(setLoading(false));
    trackFinderLoad(data, currentPage, payload);
  };

export const reset = (): AppThunk => (dispatch) => {
  dispatch(resetCommonState());
  dispatch(setFinderUsers([]));
  dispatch(setVideocastUsers([]));
  dispatch(setFilters(null));
  dispatch(setNext(null));
};
